import { Box, Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { navigate } from "gatsby";
import React from "react";
import fastCompare from "react-fast-compare";
import GeoPinIcon from "../../../../../components/icons/geo-pin";
import { getDeliveryAddressShortName } from "../../../../../helpers/express-delivery/address";
import { formatPrice } from "../../../../../helpers/price";
import { getProductDetailsPath } from "../../../../../helpers/url";
import useExpressDeliveryAvailability from "../../../../../hooks/use-express-delivery-availability";
import useFeatureFlag from "../../../../../hooks/use-feature-flag";
import useLocalizedLocationPath from "../../../../../hooks/use-localized-location-path";
import useUnlocalizedLocationPath from "../../../../../hooks/use-unlocalized-location-path";
import { ProductType } from "../../../../../types/airgraft";
import ExpressDeliveryLogo from "../../../../express-delivery/ExpressDeliveryLogo";
import ProductQuantitySelector from "../../../ProductQuantitySelector";
import {
  AddressTextButton,
  AvailabilityTypography,
  PRODUCT_DETAILS_ACTIONS_HEIGHT,
  PRODUCT_DETAILS_ACTIONS_MAX_WIDTH,
  StoreTextButton
} from "./styles";

type Props = {
  product: ProductType;
};

function ProductDetailsActions({ product }: Props) {
  const pathname = useUnlocalizedLocationPath();
  const getLocalizedPath = useLocalizedLocationPath();
  const isXpressEnabled = useFeatureFlag("SHOP_XPRESS");
  const isRetailProvidersEnabled = useFeatureFlag("SHOP_RETAIL_PROVIDERS");

  const {
    availableProvider,
    getProductExpressAvailability,
    isLoadingAvailableProducts,
    isLoadingAvailableProvider,
    address,
    toggleAvailabilityPanel,
    getAvailableProviderProductData
  } = useExpressDeliveryAvailability();

  const handleGetItDeliveredClick = () => {
    if (pathname === "/") {
      navigate(
        getLocalizedPath(
          getProductDetailsPath(product.productType, product.slug)
        )
      );
    } else {
      toggleAvailabilityPanel();
    }
  };

  const isLoadingAvailability =
    isLoadingAvailableProducts || isLoadingAvailableProvider;
  const availability = getProductExpressAvailability(product);

  // Actions skeleton while loading availability
  if (
    isLoadingAvailability &&
    availability !== "COMING_SOON" &&
    !isRetailProvidersEnabled
  ) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems={{ xs: "center", sm: "flex-start" }}
        maxWidth={{ xs: "none", sm: PRODUCT_DETAILS_ACTIONS_MAX_WIDTH }}
      >
        <Skeleton
          variant="rect"
          style={{
            width: "100%",
            height: PRODUCT_DETAILS_ACTIONS_HEIGHT,
            borderRadius: PRODUCT_DETAILS_ACTIONS_HEIGHT
          }}
        />
        <Box width="100%" mt={{ xs: 0.5, sm: 1 }}>
          <Skeleton
            variant="rect"
            style={{ width: "65%", height: "14px", borderRadius: "14px" }}
          />
        </Box>
        <Box width="100%" mt={{ xs: 0.5, sm: 1 }}>
          <Skeleton
            variant="rect"
            style={{ width: "45%", height: "14px", borderRadius: "14px" }}
          />
        </Box>
      </Box>
    );
  }

  // Express disabled
  if (!isXpressEnabled || isRetailProvidersEnabled) {
    return null;
  }

  // XPRESS enabled
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={{ xs: "center", sm: "flex-start" }}
    >
      {/* AGX (Available, quantity selector) */}
      {availability === "AVAILABLE" && availableProvider && (
        <Box alignSelf="stretch">
          {/* + 1 - Quantity Selector */}
          <Box maxWidth={{ xs: "none", sm: PRODUCT_DETAILS_ACTIONS_MAX_WIDTH }}>
            <ProductQuantitySelector
              product={product}
              maxQuantity={
                getAvailableProviderProductData(product)?.availableQuantity || 0
              }
            />
          </Box>

          {/* FREE delivery with orders over $50 */}
          <Box mt={2} textAlign={{ xs: "center", sm: "left" }}>
            <AvailabilityTypography>
              {availableProvider && (
                <strong className="orange">
                  {`FREE delivery with orders over ${formatPrice(
                    { amount: availableProvider.deliveryTotalMinimum || 0 },
                    { hideZeros: true }
                  )}.`}
                </strong>
              )}
            </AvailabilityTypography>
          </Box>
        </Box>
      )}

      {/* AGX (Not available for current provider) */}
      {(availability === "SOLDOUT" || availability === "UNAVAILABLE") && (
        <Box
          width="100%"
          maxWidth={{ xs: "none", sm: PRODUCT_DETAILS_ACTIONS_MAX_WIDTH }}
          mb={2}
        >
          <Button
            variant="contained"
            fullWidth
            style={{ minHeight: PRODUCT_DETAILS_ACTIONS_HEIGHT }}
            disabled
          >
            {availability === "SOLDOUT" && "Out of stock"}
            {availability === "UNAVAILABLE" && "Product Unavailable"}
          </Button>
        </Box>
      )}

      {/* AGX (Not available, No Address) */}
      {availability === "NO_ADDRESS" && !address && (
        <>
          <Box
            width="100%"
            maxWidth={{ xs: "none", sm: PRODUCT_DETAILS_ACTIONS_MAX_WIDTH }}
          >
            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={handleGetItDeliveredClick}
              className="express-delivery"
              endIcon={<ExpressDeliveryLogo height="10px" variant="white" />}
              style={{ minHeight: PRODUCT_DETAILS_ACTIONS_HEIGHT }}
              data-testid="express-delivery-add-location"
              data-analytics="product-details.get-it-delivered"
            >
              Get it delivered with
            </Button>
          </Box>

          <Box mt={1.5}>
            <AvailabilityTypography>
              XPRESS delivery is fullfilled by delivering dispensaries.
            </AvailabilityTypography>
          </Box>
        </>
      )}

      {/* Not available for delivery with current provider */}
      {availability === "UNAVAILABLE" && address && availableProvider && (
        <Box textAlign={{ xs: "center", sm: "left" }}>
          <AvailabilityTypography>
            This product is not available for delivery at
            <AddressTextButton onClick={() => toggleAvailabilityPanel(true)}>
              <GeoPinIcon fontSize="small" />
              {getDeliveryAddressShortName(address)}
            </AddressTextButton>
            {". "}
            <StoreTextButton onClick={() => toggleAvailabilityPanel(true)}>
              Try a different address
            </StoreTextButton>
          </AvailabilityTypography>
        </Box>
      )}

      {/* Not available for delivery (No available provider, out of range) */}
      {availability === "UNAVAILABLE" && address && !availableProvider && (
        <Box textAlign={{ xs: "center", sm: "left" }}>
          <AvailabilityTypography>
            <AddressTextButton onClick={() => toggleAvailabilityPanel(true)}>
              <GeoPinIcon fontSize="small" />
              {getDeliveryAddressShortName(address)}
            </AddressTextButton>
            {" is out of delivery range. "}
            <StoreTextButton onClick={() => toggleAvailabilityPanel(true)}>
              Try a different address
            </StoreTextButton>
          </AvailabilityTypography>
        </Box>
      )}

      {/* Not available for delivery (Soldout) */}
      {availability === "SOLDOUT" && address && (
        <Box textAlign={{ xs: "center", sm: "left" }}>
          <AvailabilityTypography>
            <div>
              {"This product is out of stock at the delivering dispensary "}
              <strong>{availableProvider.publicName}</strong>
            </div>
            <StoreTextButton onClick={() => toggleAvailabilityPanel(true)}>
              Try a different address
            </StoreTextButton>
          </AvailabilityTypography>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(ProductDetailsActions, fastCompare);
